import type { Channel } from '@sbc/domain-models/gateway';
import {
  Facebook,
  Hash,
  Instagram,
  type LucideIcon,
  MessageCircle,
  MessageSquareText,
  MessagesSquare,
  Music2,
  Smartphone,
  Users,
} from 'lucide-react';

import { cn } from '@/lib/utils';

import { CHANNEL_NAMES, avatarTone, initialsOf } from './inbox-format';

const CHANNEL_ICONS: Record<Channel, { icon: LucideIcon; tone: string }> = {
  instagram: { icon: Instagram, tone: 'text-pink-600 dark:text-pink-400' },
  facebook: { icon: Facebook, tone: 'text-blue-600 dark:text-blue-400' },
  intercom: { icon: MessagesSquare, tone: 'text-sky-600 dark:text-sky-400' },
  slack: { icon: Hash, tone: 'text-violet-600 dark:text-violet-400' },
  sbc: { icon: Smartphone, tone: 'text-[var(--color-foreground)]' },
  tiktok: { icon: Music2, tone: 'text-[var(--color-foreground)]' },
  facebook_group: { icon: Users, tone: 'text-blue-600 dark:text-blue-400' },
  quo: { icon: MessageSquareText, tone: 'text-emerald-600 dark:text-emerald-400' },
  whatsapp: { icon: MessageCircle, tone: 'text-green-600 dark:text-green-400' },
  klaviyo_sms: { icon: MessageSquareText, tone: 'text-emerald-600 dark:text-emerald-400' },
};

export function ChannelIcon({ channel, className }: { channel: Channel; className?: string }) {
  const { icon: Icon, tone } = CHANNEL_ICONS[channel];
  return (
    <>
      <Icon className={cn('size-3.5 shrink-0', tone, className)} aria-hidden="true" />
      <span className="sr-only">{CHANNEL_NAMES[channel]}</span>
    </>
  );
}

export function InboxAvatar({
  name,
  channel,
  size = 'md',
}: {
  name: string;
  channel: Channel;
  size?: 'md' | 'lg';
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <span
        className={cn(
          'inline-flex items-center justify-center rounded-full font-semibold',
          size === 'lg' ? 'size-12 text-base' : 'size-10 text-sm',
          avatarTone(name),
        )}
        aria-hidden="true"
      >
        {initialsOf(name)}
      </span>
      <span className="absolute -right-0.5 -bottom-0.5 inline-flex size-5 items-center justify-center rounded-full border bg-[var(--color-background)]">
        <ChannelIcon channel={channel} className="size-3" />
      </span>
    </span>
  );
}
